import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { OverlayPanel } from 'primereact/overlaypanel'
import { Slider } from 'primereact/slider'
import { classNames } from 'primereact/utils'
import {
  FC,
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import toast from 'react-hot-toast'

/** An image the user can pick to mark up. */
export type MarkupImage = { url: string; name: string }

type MarkupModalProps = {
  isOpen: boolean
  onClose: () => void
  /** Images available to annotate (e.g. the run's post-proc / geometry images). */
  images: MarkupImage[]
  /** Called with the flattened PNG when the user saves. */
  onSave: (file: File, caption: string) => void | Promise<void>
  saving?: boolean
}

type Tool = 'pen' | 'highlighter' | 'eraser' | 'crop' | 'shape'

export type ShapeKind =
  | 'line'
  | 'arrow'
  | 'wave'
  | 'rect'
  | 'roundRect'
  | 'ellipse'
  | 'triangle'
  | 'rightTriangle'
  | 'parallelogram'
  | 'diamond'
  | 'pentagon'
  | 'hexagon'
  | 'star4'
  | 'star5'
  | 'arrowRight'
  | 'arrowLeft'
  | 'arrowUp'
  | 'arrowDown'
  | 'speech'

/** Shapes drawn as an unclosed stroke — they are never filled. */
const OPEN_SHAPES = new Set<ShapeKind>(['line', 'arrow', 'wave'])

const SHAPES: { kind: ShapeKind; label: string }[] = [
  { kind: 'line', label: 'Line' },
  { kind: 'arrow', label: 'Arrow' },
  { kind: 'wave', label: 'Curve' },
  { kind: 'rect', label: 'Rectangle' },
  { kind: 'roundRect', label: 'Rounded rectangle' },
  { kind: 'ellipse', label: 'Ellipse' },
  { kind: 'triangle', label: 'Triangle' },
  { kind: 'rightTriangle', label: 'Right triangle' },
  { kind: 'parallelogram', label: 'Parallelogram' },
  { kind: 'diamond', label: 'Diamond' },
  { kind: 'pentagon', label: 'Pentagon' },
  { kind: 'hexagon', label: 'Hexagon' },
  { kind: 'arrowRight', label: 'Block arrow — right' },
  { kind: 'arrowLeft', label: 'Block arrow — left' },
  { kind: 'arrowUp', label: 'Block arrow — up' },
  { kind: 'arrowDown', label: 'Block arrow — down' },
  { kind: 'star4', label: 'Four-point star' },
  { kind: 'star5', label: 'Five-point star' },
  { kind: 'speech', label: 'Speech bubble' }
]

const r2 = (v: number) => Math.round(v * 100) / 100

const polyD = (pts: [number, number][]) =>
  `${pts.map(([px, py], i) => `${i ? 'L' : 'M'} ${r2(px)} ${r2(py)}`).join(' ')} Z`

/** Unit-square (0..1) polygon mapped onto the bounding box. */
const unitPolyD = (x: number, y: number, w: number, h: number, unit: [number, number][]) =>
  polyD(unit.map(([u, v]) => [x + u * w, y + v * h]))

const regularPolyD = (x: number, y: number, w: number, h: number, sides: number) => {
  const cx = x + w / 2
  const cy = y + h / 2
  const pts: [number, number][] = []
  for (let i = 0; i < sides; i++) {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / sides
    pts.push([cx + (w / 2) * Math.cos(a), cy + (h / 2) * Math.sin(a)])
  }
  return polyD(pts)
}

const starPolyD = (
  x: number,
  y: number,
  w: number,
  h: number,
  points: number,
  innerRatio: number
) => {
  const cx = x + w / 2
  const cy = y + h / 2
  const pts: [number, number][] = []
  for (let i = 0; i < points * 2; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / points
    const k = i % 2 === 0 ? 1 : innerRatio
    pts.push([cx + (w / 2) * k * Math.cos(a), cy + (h / 2) * k * Math.sin(a)])
  }
  return polyD(pts)
}

const BLOCK_ARROW_UNIT: [number, number][] = [
  [0, 0.3],
  [0.55, 0.3],
  [0.55, 0.04],
  [1, 0.5],
  [0.55, 0.96],
  [0.55, 0.7],
  [0, 0.7]
]

const rotateUnit = (
  unit: [number, number][],
  dir: 'right' | 'left' | 'up' | 'down'
): [number, number][] => {
  switch (dir) {
    case 'left':
      return unit.map(([u, v]) => [1 - u, v])
    case 'up':
      return unit.map(([u, v]) => [v, 1 - u])
    case 'down':
      return unit.map(([u, v]) => [1 - v, u])
    default:
      return unit
  }
}

/**
 * SVG path data for a shape dragged from (x0,y0) to (x1,y1). Shared by the canvas
 * (via `Path2D`) and the picker icons so both stay in sync.
 */
const shapeD = (kind: ShapeKind, x0: number, y0: number, x1: number, y1: number): string => {
  const x = Math.min(x0, x1)
  const y = Math.min(y0, y1)
  const w = Math.abs(x1 - x0)
  const h = Math.abs(y1 - y0)

  switch (kind) {
    case 'line':
      return `M ${r2(x0)} ${r2(y0)} L ${r2(x1)} ${r2(y1)}`
    case 'arrow': {
      const dist = Math.hypot(x1 - x0, y1 - y0) || 1
      const head = Math.min(Math.max(dist * 0.22, 4), 40)
      const a = Math.atan2(y1 - y0, x1 - x0)
      const wing = (sign: number) => {
        const wa = a + sign * Math.PI * 0.82
        return `M ${r2(x1)} ${r2(y1)} L ${r2(x1 + head * Math.cos(wa))} ${r2(
          y1 + head * Math.sin(wa)
        )}`
      }
      return `M ${r2(x0)} ${r2(y0)} L ${r2(x1)} ${r2(y1)} ${wing(1)} ${wing(-1)}`
    }
    case 'wave': {
      const cy = y + h / 2
      return `M ${r2(x)} ${r2(cy)} C ${r2(x + w * 0.15)} ${r2(y)} ${r2(x + w * 0.35)} ${r2(
        y
      )} ${r2(x + w * 0.5)} ${r2(cy)} C ${r2(x + w * 0.65)} ${r2(y + h)} ${r2(
        x + w * 0.85
      )} ${r2(y + h)} ${r2(x + w)} ${r2(cy)}`
    }
    case 'rect':
      return unitPolyD(x, y, w, h, [
        [0, 0],
        [1, 0],
        [1, 1],
        [0, 1]
      ])
    case 'roundRect': {
      const r = r2(Math.min(w, h) * 0.18)
      return `M ${r2(x + r)} ${r2(y)} H ${r2(x + w - r)} A ${r} ${r} 0 0 1 ${r2(x + w)} ${r2(
        y + r
      )} V ${r2(y + h - r)} A ${r} ${r} 0 0 1 ${r2(x + w - r)} ${r2(y + h)} H ${r2(
        x + r
      )} A ${r} ${r} 0 0 1 ${r2(x)} ${r2(y + h - r)} V ${r2(y + r)} A ${r} ${r} 0 0 1 ${r2(
        x + r
      )} ${r2(y)} Z`
    }
    case 'ellipse': {
      const rx = r2(w / 2)
      const ry = r2(h / 2)
      const cy = r2(y + h / 2)
      return `M ${r2(x)} ${cy} A ${rx} ${ry} 0 1 0 ${r2(x + w)} ${cy} A ${rx} ${ry} 0 1 0 ${r2(
        x
      )} ${cy} Z`
    }
    case 'triangle':
      return unitPolyD(x, y, w, h, [
        [0.5, 0],
        [1, 1],
        [0, 1]
      ])
    case 'rightTriangle':
      return unitPolyD(x, y, w, h, [
        [0, 0],
        [1, 1],
        [0, 1]
      ])
    case 'parallelogram':
      return unitPolyD(x, y, w, h, [
        [0.25, 0],
        [1, 0],
        [0.75, 1],
        [0, 1]
      ])
    case 'diamond':
      return unitPolyD(x, y, w, h, [
        [0.5, 0],
        [1, 0.5],
        [0.5, 1],
        [0, 0.5]
      ])
    case 'pentagon':
      return regularPolyD(x, y, w, h, 5)
    case 'hexagon':
      return regularPolyD(x, y, w, h, 6)
    case 'star4':
      return starPolyD(x, y, w, h, 4, 0.38)
    case 'star5':
      return starPolyD(x, y, w, h, 5, 0.382)
    case 'arrowRight':
    case 'arrowLeft':
    case 'arrowUp':
    case 'arrowDown': {
      const dir = kind.replace('arrow', '').toLowerCase() as 'right' | 'left' | 'up' | 'down'
      return unitPolyD(x, y, w, h, rotateUnit(BLOCK_ARROW_UNIT, dir))
    }
    case 'speech': {
      const bh = h * 0.78
      const r = r2(Math.min(w, bh) * 0.18)
      return `M ${r2(x + r)} ${r2(y)} H ${r2(x + w - r)} A ${r} ${r} 0 0 1 ${r2(x + w)} ${r2(
        y + r
      )} V ${r2(y + bh - r)} A ${r} ${r} 0 0 1 ${r2(x + w - r)} ${r2(y + bh)} L ${r2(
        x + w * 0.42
      )} ${r2(y + bh)} L ${r2(x + w * 0.2)} ${r2(y + h)} L ${r2(x + w * 0.3)} ${r2(
        y + bh
      )} L ${r2(x + r)} ${r2(y + bh)} A ${r} ${r} 0 0 1 ${r2(x)} ${r2(y + bh - r)} V ${r2(
        y + r
      )} A ${r} ${r} 0 0 1 ${r2(x + r)} ${r2(y)} Z`
    }
    default:
      return ''
  }
}

const ShapeIcon: FC<{ kind: ShapeKind; className?: string }> = ({ kind, className }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <path d={shapeD(kind, 3, 4, 21, 20)} />
  </svg>
)

const COLORS = [
  '#ff3b30',
  '#ff9500',
  '#ffcc00',
  '#34c759',
  '#0a84ff',
  '#af52de',
  '#000000',
  '#ffffff'
]
const COLOR_NAMES: Record<string, string> = {
  '#ff3b30': 'Red',
  '#ff9500': 'Orange',
  '#ffcc00': 'Yellow',
  '#34c759': 'Green',
  '#0a84ff': 'Blue',
  '#af52de': 'Purple',
  '#000000': 'Black',
  '#ffffff': 'White'
}
const MAX_CANVAS_WIDTH = 1100
const MAX_HISTORY = 25

const MarkupModal: FC<MarkupModalProps> = ({ isOpen, onClose, images, onSave, saving }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  // Offscreen copy of the committed canvas, used for crop preview redraws.
  const cropBaseRef = useRef<HTMLCanvasElement | null>(null)
  // Offscreen copy of the committed canvas, used for live shape preview redraws.
  const shapeBaseRef = useRef<HTMLCanvasElement | null>(null)
  const shapePanelRef = useRef<OverlayPanel>(null)

  const [selectedUrl, setSelectedUrl] = useState<string | null>(null)
  const [tool, setTool] = useState<Tool>('pen')
  const [shape, setShape] = useState<ShapeKind>('rect')
  const [fillShape, setFillShape] = useState(false)
  const [color, setColor] = useState('#ff3b30')
  const [lineWidth, setLineWidth] = useState(4)
  const [caption, setCaption] = useState('')
  const [history, setHistory] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [cropRect, setCropRect] = useState<{ x: number; y: number; w: number; h: number } | null>(
    null
  )

  const drawing = useRef(false)
  const start = useRef<{ x: number; y: number } | null>(null)

  const selectedName = images.find(i => i.url === selectedUrl)?.name ?? 'markup'

  // Reset when the modal opens; default to the first image (and drop a stale
  // selection that isn't in the current picker's images).
  useEffect(() => {
    if (!isOpen) return
    setSelectedUrl(prev =>
      prev && images.some(i => i.url === prev) ? prev : (images[0]?.url ?? null)
    )
    setTool('pen')
    setCaption('')
    setCropRect(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const pushHistory = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    try {
      const url = canvas.toDataURL('image/png')
      setHistory(prev => [...prev, url].slice(-MAX_HISTORY))
    } catch {
      /* tainted canvas — undo won't be available but drawing still works */
    }
  }, [])

  // Load the selected image onto the canvas, sized to fit.
  useEffect(() => {
    if (!isOpen || !selectedUrl) return
    const canvas = canvasRef.current
    if (!canvas) return
    setLoading(true)
    setCropRect(null)
    const img = new Image()
    img.crossOrigin = 'anonymous'
    const draw = () => {
      const scale = Math.min(1, MAX_CANVAS_WIDTH / img.naturalWidth)
      canvas.width = Math.round(img.naturalWidth * scale)
      canvas.height = Math.round(img.naturalHeight * scale)
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      setLoading(false)
      try {
        setHistory([canvas.toDataURL('image/png')])
      } catch {
        setHistory([])
      }
    }
    img.onload = draw
    img.onerror = () => {
      // Retry without CORS so the user can at least annotate (export may fail).
      const fallback = new Image()
      fallback.onload = () => {
        const scale = Math.min(1, MAX_CANVAS_WIDTH / fallback.naturalWidth)
        canvas.width = Math.round(fallback.naturalWidth * scale)
        canvas.height = Math.round(fallback.naturalHeight * scale)
        const ctx = canvas.getContext('2d')
        if (ctx) ctx.drawImage(fallback, 0, 0, canvas.width, canvas.height)
        setLoading(false)
        setHistory([])
      }
      fallback.onerror = () => {
        setLoading(false)
        toast.error('Could not load image for markup')
      }
      fallback.src = selectedUrl
    }
    img.src = selectedUrl
  }, [isOpen, selectedUrl])

  const strokeStyle = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      if (tool === 'eraser') {
        ctx.globalCompositeOperation = 'destination-out'
        ctx.globalAlpha = 1
        ctx.lineWidth = lineWidth * 4
        ctx.strokeStyle = 'rgba(0,0,0,1)'
      } else if (tool === 'highlighter') {
        ctx.globalCompositeOperation = 'source-over'
        ctx.globalAlpha = 0.35
        ctx.lineWidth = lineWidth * 4
        ctx.strokeStyle = color
      } else {
        ctx.globalCompositeOperation = 'source-over'
        ctx.globalAlpha = 1
        ctx.lineWidth = lineWidth
        ctx.strokeStyle = color
      }
    },
    [tool, color, lineWidth]
  )

  const drawShape = useCallback(
    (ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number) => {
      const d = shapeD(shape, x0, y0, x1, y1)
      if (!d) return
      const path = new Path2D(d)
      ctx.save()
      ctx.globalCompositeOperation = 'source-over'
      ctx.globalAlpha = 1
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.lineWidth = lineWidth
      ctx.strokeStyle = color
      if (fillShape && !OPEN_SHAPES.has(shape)) {
        ctx.fillStyle = color
        ctx.fill(path)
      }
      ctx.stroke(path)
      ctx.restore()
    },
    [shape, fillShape, color, lineWidth]
  )

  const posFromEvent = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!
    const rect = canvas.getBoundingClientRect()
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height
    }
  }

  /** Shift-drag: square/circle bounds, or 45° increments for line-like shapes. */
  const constrained = (
    s: { x: number; y: number },
    p: { x: number; y: number },
    shiftKey: boolean
  ) => {
    if (!shiftKey) return p
    const dx = p.x - s.x
    const dy = p.y - s.y
    if (shape === 'line' || shape === 'arrow') {
      const dist = Math.hypot(dx, dy)
      const a = (Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * Math.PI) / 4
      return { x: s.x + dist * Math.cos(a), y: s.y + dist * Math.sin(a) }
    }
    const size = Math.max(Math.abs(dx), Math.abs(dy))
    return { x: s.x + Math.sign(dx || 1) * size, y: s.y + Math.sign(dy || 1) * size }
  }

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas || loading) return
    canvas.setPointerCapture(e.pointerId)
    drawing.current = true
    const p = posFromEvent(e)
    start.current = p
    if (tool === 'crop') {
      // Snapshot current pixels so we can redraw under the selection rectangle.
      const base = document.createElement('canvas')
      base.width = canvas.width
      base.height = canvas.height
      base.getContext('2d')?.drawImage(canvas, 0, 0)
      cropBaseRef.current = base
      setCropRect({ x: p.x, y: p.y, w: 0, h: 0 })
      return
    }
    if (tool === 'shape') {
      // Snapshot current pixels so the shape preview can be redrawn on each move.
      const base = document.createElement('canvas')
      base.width = canvas.width
      base.height = canvas.height
      base.getContext('2d')?.drawImage(canvas, 0, 0)
      shapeBaseRef.current = base
      return
    }
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    strokeStyle(ctx)
    ctx.beginPath()
    ctx.moveTo(p.x, p.y)
    ctx.lineTo(p.x + 0.01, p.y + 0.01)
    ctx.stroke()
  }

  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const p = posFromEvent(e)
    if (tool === 'crop') {
      const s = start.current!
      const base = cropBaseRef.current
      ctx.globalCompositeOperation = 'source-over'
      ctx.globalAlpha = 1
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      if (base) ctx.drawImage(base, 0, 0)
      const x = Math.min(s.x, p.x)
      const y = Math.min(s.y, p.y)
      const w = Math.abs(p.x - s.x)
      const h = Math.abs(p.y - s.y)
      ctx.save()
      ctx.fillStyle = 'rgba(0,0,0,0.35)'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.clearRect(x, y, w, h)
      if (base) ctx.drawImage(base, x, y, w, h, x, y, w, h)
      ctx.setLineDash([6, 4])
      ctx.strokeStyle = '#0a84ff'
      ctx.lineWidth = 2
      ctx.strokeRect(x, y, w, h)
      ctx.restore()
      setCropRect({ x, y, w, h })
      return
    }
    if (tool === 'shape') {
      const s = start.current!
      const end = constrained(s, p, e.shiftKey)
      ctx.globalCompositeOperation = 'source-over'
      ctx.globalAlpha = 1
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      if (shapeBaseRef.current) ctx.drawImage(shapeBaseRef.current, 0, 0)
      drawShape(ctx, s.x, s.y, end.x, end.y)
      return
    }
    strokeStyle(ctx)
    ctx.lineTo(p.x, p.y)
    ctx.stroke()
  }

  const onPointerUp = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return
    drawing.current = false
    canvasRef.current?.releasePointerCapture(e.pointerId)
    if (tool === 'crop') return // wait for Apply Crop
    if (tool === 'shape') {
      const canvas = canvasRef.current
      const ctx = canvas?.getContext('2d')
      const s = start.current
      if (canvas && ctx && s) {
        const p = constrained(s, posFromEvent(e), e.shiftKey)
        const tooSmall = Math.abs(p.x - s.x) < 2 && Math.abs(p.y - s.y) < 2
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        if (shapeBaseRef.current) ctx.drawImage(shapeBaseRef.current, 0, 0)
        if (!tooSmall) {
          drawShape(ctx, s.x, s.y, p.x, p.y)
          pushHistory()
        }
      }
      shapeBaseRef.current = null
      return
    }
    const ctx = canvasRef.current?.getContext('2d')
    ctx?.closePath()
    pushHistory()
  }

  const applyCrop = () => {
    const canvas = canvasRef.current
    const base = cropBaseRef.current
    if (!canvas || !base || !cropRect || cropRect.w < 4 || cropRect.h < 4) {
      setCropRect(null)
      // Restore the un-dimmed image.
      if (canvas && base) canvas.getContext('2d')?.drawImage(base, 0, 0)
      return
    }
    const { x, y, w, h } = cropRect
    const out = document.createElement('canvas')
    out.width = Math.round(w)
    out.height = Math.round(h)
    out.getContext('2d')?.drawImage(base, x, y, w, h, 0, 0, out.width, out.height)
    canvas.width = out.width
    canvas.height = out.height
    const ctx = canvas.getContext('2d')
    ctx?.clearRect(0, 0, canvas.width, canvas.height)
    ctx?.drawImage(out, 0, 0)
    cropBaseRef.current = null
    setCropRect(null)
    setTool('pen')
    pushHistory()
  }

  const cancelCrop = () => {
    const canvas = canvasRef.current
    const base = cropBaseRef.current
    if (canvas && base) {
      const ctx = canvas.getContext('2d')
      ctx?.clearRect(0, 0, canvas.width, canvas.height)
      ctx?.drawImage(base, 0, 0)
    }
    cropBaseRef.current = null
    setCropRect(null)
  }

  const drawDataUrl = useCallback((url: string) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const img = new Image()
    img.onload = () => {
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')
      ctx?.clearRect(0, 0, canvas.width, canvas.height)
      ctx?.drawImage(img, 0, 0)
    }
    img.src = url
  }, [])

  const undo = useCallback(() => {
    if (history.length < 2) return
    const next = history.slice(0, -1)
    setHistory(next)
    drawDataUrl(next[next.length - 1])
    setCropRect(null)
    cropBaseRef.current = null
    shapeBaseRef.current = null
  }, [history, drawDataUrl])

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'z' || e.shiftKey || !(e.ctrlKey || e.metaKey)) return
      // Let the caption field keep its own text undo.
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
      e.preventDefault()
      undo()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, undo])

  const resetToOriginal = () => {
    if (!history.length) return
    drawDataUrl(history[0])
    setHistory(h => (h.length ? [h[0]] : h))
    setCropRect(null)
    cropBaseRef.current = null
    shapeBaseRef.current = null
  }

  const handleSave = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    try {
      canvas.toBlob(blob => {
        if (!blob) {
          toast.error('Could not export the marked-up image (source may block cross-origin reads)')
          return
        }
        const safe = selectedName.replace(/\.[a-z0-9]+$/i, '')
        const file = new File([blob], `${safe}_markup.png`, { type: 'image/png' })
        void onSave(file, caption.trim() || `${selectedName} (markup)`)
      }, 'image/png')
    } catch {
      toast.error('Could not export the marked-up image (source may block cross-origin reads)')
    }
  }

  const toolBtn = (t: Tool, icon: string, tooltip: string) => (
    <Button
      icon={icon}
      tooltip={tooltip}
      tooltipOptions={{ position: 'top' }}
      outlined={tool !== t}
      onClick={() => {
        if (tool === 'crop' && t !== 'crop') cancelCrop()
        setTool(t)
      }}
    />
  )

  const activeShapeLabel = SHAPES.find(s => s.kind === shape)?.label ?? 'Shape'

  return (
    <Dialog
      header="Markup Image"
      visible={isOpen}
      onHide={onClose}
      style={{ width: '82vw', maxWidth: 1200 }}
      modal>
      <div className="flex gap-4 min-h-0">
        {/* Image picker */}
        <div className="w-40 shrink-0 max-h-[62vh] overflow-y-auto space-y-2 pr-1">
          {images.length === 0 && (
            <p className="text-xs text-gray-500 dark:text-neutral-60">No images available.</p>
          )}
          {images.map(img => (
            <button
              key={img.url}
              onClick={() => setSelectedUrl(img.url)}
              title={img.name}
              className={classNames('block w-full rounded overflow-hidden border-2', {
                'border-gm-blue': selectedUrl === img.url,
                'border-transparent hover:border-gray-300': selectedUrl !== img.url
              })}>
              <img src={img.url} alt={img.name} className="w-full h-20 object-cover" />
            </button>
          ))}
        </div>

        {/* Canvas + tools */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Toolbar */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="flex items-center gap-1">
              {toolBtn('pen', 'pi pi-pencil', 'Draw freehand')}
              {toolBtn('highlighter', 'pi pi-pen-to-square', 'Highlight an area')}
              {toolBtn('eraser', 'pi pi-eraser', 'Erase marks')}
              {toolBtn('crop', 'pi pi-clone', 'Crop the image')}
              <Button
                tooltip={`Add a shape (${activeShapeLabel}) — click again to change shape`}
                tooltipOptions={{ position: 'top' }}
                aria-label="Add a shape"
                outlined={tool !== 'shape'}
                onClick={e => {
                  if (tool === 'crop') cancelCrop()
                  setTool('shape')
                  shapePanelRef.current?.toggle(e)
                }}>
                <ShapeIcon kind={shape} className="w-4 h-4" />
                <i className="pi pi-chevron-down text-[0.6rem] ml-1" />
              </Button>
            </div>

            <OverlayPanel ref={shapePanelRef}>
              <div className="grid grid-cols-5 gap-1 w-[15rem]">
                {SHAPES.map(s => (
                  <button
                    key={s.kind}
                    type="button"
                    title={s.label}
                    aria-label={s.label}
                    onClick={() => {
                      setShape(s.kind)
                      setTool('shape')
                      shapePanelRef.current?.hide()
                    }}
                    className={classNames(
                      'flex items-center justify-center p-1.5 rounded border text-gray-700 dark:text-neutral-20',
                      {
                        'border-gm-blue bg-gm-blue/10': shape === s.kind,
                        'border-transparent hover:bg-gray-100 dark:hover:bg-neutral-70':
                          shape !== s.kind
                      }
                    )}>
                    <ShapeIcon kind={s.kind} className="w-5 h-5" />
                  </button>
                ))}
              </div>
              <div className="mt-2 pt-2 border-t border-gray-200 dark:border-neutral-70">
                <Button
                  label="Fill shape"
                  icon={fillShape ? 'pi pi-check-square' : 'pi pi-stop'}
                  size="small"
                  text
                  onClick={() => setFillShape(v => !v)}
                  tooltip="Fill closed shapes with the selected color"
                  tooltipOptions={{ position: 'top' }}
                />
              </div>
            </OverlayPanel>

            <span className="w-px h-6 bg-gray-300 dark:bg-neutral-60 mx-1" />

            {/* Colors */}
            <div className="flex items-center gap-1">
              {COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  title={`${COLOR_NAMES[c] ?? c} pen color`}
                  className={classNames('w-5 h-5 rounded-full border', {
                    'ring-2 ring-offset-1 ring-gm-blue': color === c,
                    'border-gray-300': color !== c
                  })}
                  style={{ backgroundColor: c }}
                />
              ))}
              <input
                type="color"
                value={color}
                onChange={e => setColor(e.target.value)}
                className="w-6 h-6 p-0 border-0 bg-transparent cursor-pointer"
                title="Pick a custom pen color"
              />
            </div>

            <span className="w-px h-6 bg-gray-300 dark:bg-neutral-60 mx-1" />

            {/* Stroke width */}
            <div className="flex items-center gap-2 w-40" title="Adjust the stroke width">
              <i className="pi pi-minus text-xs text-gray-500" />
              <Slider
                value={lineWidth}
                min={1}
                max={30}
                onChange={e => setLineWidth(e.value as number)}
                className="flex-1"
              />
              <span className="text-xs text-gray-500 w-6 text-right">{lineWidth}</span>
            </div>

            <span className="flex-1" />

            <Button
              icon="pi pi-undo"
              tooltip="Undo the last change (Ctrl+Z)"
              tooltipOptions={{ position: 'top' }}
              outlined
              disabled={history.length < 2}
              onClick={undo}
            />
            <Button
              icon="pi pi-refresh"
              tooltip="Discard all edits and start over"
              tooltipOptions={{ position: 'top' }}
              outlined
              disabled={history.length === 0}
              onClick={resetToOriginal}
            />
          </div>

          {tool === 'shape' && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-gray-500 dark:text-neutral-60">
                Drag on the image to draw a {activeShapeLabel.toLowerCase()}. Hold Shift to keep it
                regular.
              </span>
            </div>
          )}

          {/* Crop actions */}
          {tool === 'crop' && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-gray-500 dark:text-neutral-60">
                Drag on the image to select a crop region.
              </span>
              <Button
                label="Apply Crop"
                icon="pi pi-check"
                size="small"
                onClick={applyCrop}
                tooltip="Crop the image to the selected region"
                tooltipOptions={{ position: 'top' }}
              />
              <Button
                label="Cancel"
                icon="pi pi-times"
                size="small"
                text
                onClick={cancelCrop}
                tooltip="Discard the crop selection"
                tooltipOptions={{ position: 'top' }}
              />
            </div>
          )}

          {/* Canvas */}
          <div className="relative flex-1 min-h-0 flex items-center justify-center bg-neutral-10 dark:bg-neutral-90 rounded border border-gray-200 dark:border-neutral-70 overflow-auto">
            {loading && (
              <span className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">
                Loading…
              </span>
            )}
            <canvas
              ref={canvasRef}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              className="max-w-full max-h-[52vh] touch-none"
              style={{ cursor: tool === 'crop' ? 'crosshair' : 'crosshair' }}
            />
          </div>

          {/* Footer */}
          <div className="flex items-center gap-2 mt-3">
            <InputText
              value={caption}
              onChange={e => setCaption(e.target.value)}
              placeholder="Caption (optional)"
              className="flex-1 text-sm"
            />
            <Button
              label="Cancel"
              text
              onClick={onClose}
              tooltip="Close without saving"
              tooltipOptions={{ position: 'top' }}
            />
            <Button
              label="Save to Run Media"
              icon="pi pi-save"
              onClick={handleSave}
              loading={saving}
              disabled={!selectedUrl || loading}
              tooltip="Save this marked-up image to the run's media library"
              tooltipOptions={{ position: 'top' }}
            />
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default MarkupModal
